import React from "react";
import { BarChart3, TrendingDown, TrendingUp } from "lucide-react";
import { useWeeklyScanHistorySummary } from "./useScanHistorySummary";

interface OptimisationReportProps {
  venueId: string | undefined;
}

export const OptimisationReport = ({
  venueId
}: OptimisationReportProps): React.ReactElement | null => {
  const { summary, isLoading } = useWeeklyScanHistorySummary(venueId);

  if (isLoading || !summary || summary.totalScans === 0) {
    return null;
  }

  const utilisationPercent = Math.round(
    summary.averageUtilisationImprovement * 100
  );
  const scoreTrend = summary.averageScore - summary.historicalAverageScore;
  const TrendIcon = scoreTrend >= 0 ? TrendingUp : TrendingDown;
  const trendColour =
    scoreTrend >= 0
      ? "text-[var(--successColor,#22c55e)]"
      : "text-[var(--destructiveColor,#ef4444)]";

  return (
    <section
      className="my-4 rounded-lg border border-[var(--borderColor,#e5e7eb)] p-3"
      aria-label="Optimisation report"
    >
      <div className="mb-3 flex items-center gap-2">
        <BarChart3
          className="size-4 flex-shrink-0 text-[var(--infoColor,#3b82f6)]"
          aria-hidden="true"
        />
        <h4 className="text-sm font-semibold text-[var(--primaryTextColor)]">
          Weekly Optimisation Report
        </h4>
      </div>

      <p className="mb-3 text-xs text-[var(--secondaryTextColor)]">
        This week, Space Optimiser helped you seat{" "}
        <span className="font-semibold text-[var(--primaryTextColor)]">
          {summary.totalAdditionalGuests}
        </span>{" "}
        additional guests by improving table utilisation by{" "}
        <span className="font-semibold text-[var(--primaryTextColor)]">
          {utilisationPercent}%
        </span>
        {"."}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md bg-card p-2.5">
          <p className="text-xs text-muted-foreground">Average Score</p>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-base font-bold text-[var(--primaryTextColor)]">
              {summary.averageScore}%
            </span>
            <TrendIcon
              className={`size-3.5 ${trendColour}`}
              aria-label={
                scoreTrend >= 0 ? "Score trending up" : "Score trending down"
              }
            />
          </div>
        </div>
        <div className="rounded-md bg-card p-2.5">
          <p className="text-xs text-muted-foreground">Total Scans</p>
          <p className="mt-1 text-base font-bold text-[var(--primaryTextColor)]">
            {summary.totalScans}
          </p>
        </div>
      </div>
    </section>
  );
};
